import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShareButton } from '@/components/ShareButton';
import {
  Loader2, X, ChevronLeft, ChevronRight, MapPin, Camera, ArrowLeft, ImageIcon
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function PortfolioShowcase() {
  const { slug } = useParams();
  const [showcase, setShowcase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Fetch showcase data
  useEffect(() => {
    const fetchShowcase = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}/api/portfolio/public/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Property not found in portfolio.');
          }
          throw new Error('Failed to load property');
        }
        const data = await response.json();
        setShowcase(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchShowcase();
  }, [slug]);

  // Lightbox navigation
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  const prevPhoto = useCallback(() => {
    if (!showcase?.photos) return;
    setLightboxIndex((prev) => (prev === 0 ? showcase.photos.length - 1 : prev - 1));
  }, [showcase?.photos]);

  const nextPhoto = useCallback(() => {
    if (!showcase?.photos) return;
    setLightboxIndex((prev) => (prev === showcase.photos.length - 1 ? 0 : prev + 1));
  }, [showcase?.photos]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevPhoto();
      if (e.key === 'ArrowRight') nextPhoto();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, closeLightbox, prevPhoto, nextPhoto]);

  // Touch/swipe handling
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextPhoto();
      else prevPhoto();
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-[#C6A14A] animate-spin mx-auto mb-4" />
          <p className="text-[#E8E4DE]/60 text-sm">Loading property...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center px-4 text-center">
        <div className="p-6 bg-white/5 rounded-full mb-6">
          <ImageIcon className="h-12 w-12 text-[#E8E4DE]/30" />
        </div>
        <h1 className="text-2xl font-light text-white mb-3">Property Not Found</h1>
        <p className="text-[#E8E4DE]/60 mb-8 max-w-md">{error}</p>
        <Button asChild variant="outline" className="border-[#C6A14A]/30 text-[#C6A14A] hover:bg-[#C6A14A]/10">
          <Link to="/portfolio">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Portfolio
          </Link>
        </Button>
      </div>
    );
  }

  if (!showcase) return null;

  const { city, state, photos } = showcase;
  const coverPhoto = photos[0];

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Hero Section */}
      {coverPhoto && (
        <div className="relative w-full h-[50vh] md:h-[60vh] overflow-hidden">
          <img
            src={coverPhoto.url}
            alt={`${city}, ${state}`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/40 to-transparent" />
          
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 lg:p-16">
            <div className="max-w-7xl mx-auto">
              <Badge className="mb-4 bg-[#C6A14A]/10 text-[#C6A14A] border-[#C6A14A]/30 px-3 py-1">
                Portfolio
              </Badge>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-white tracking-wide mb-4">
                {city}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-[#E8E4DE]/70">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-[#C6A14A]" />
                  <span className="text-sm">{state}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-[#C6A14A]" />
                  <span className="text-sm">{photos.length} Photos</span>
                </div>
                <ShareButton slug={slug} title={`${city}, ${state}`} variant="solid" className="relative z-10" testId="portfolio-hero-share-btn" />
              </div>
            </div>
          </div>

          <button
            onClick={() => openLightbox(0)}
            className="absolute inset-0 cursor-pointer group"
          >
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                View Full Screen
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Navigation Bar */}
      <div className="sticky top-0 z-40 bg-[#0a0a0f]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/portfolio" 
              className="flex items-center gap-2 text-[#E8E4DE]/60 hover:text-[#C6A14A] transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm">Back to Portfolio</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <span className="text-[#E8E4DE]/40 text-sm hidden sm:inline">
                {city}, {state}
              </span>
              <ShareButton slug={slug} title={`${city}, ${state}`} label="Copy Share Link" testId="public-copy-share-link-btn" />
            </div>
          </div>
        </div>
      </div>

      {/* Photo Grid - Masonry */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {photos.length === 0 ? (
          <div className="text-center py-20">
            <div className="p-6 bg-white/5 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center">
              <ImageIcon className="h-10 w-10 text-[#E8E4DE]/30" />
            </div>
            <h3 className="text-xl text-white mb-2">No Photos Available</h3>
            <p className="text-[#E8E4DE]/60">This property doesn't have any photos yet.</p>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                onClick={() => openLightbox(index)}
                className="relative break-inside-avoid cursor-pointer group rounded-lg overflow-hidden bg-[#12121a]"
                data-testid={`showcase-photo-${photo.id}`}
              >
                <img
                  src={photo.url}
                  alt={`Property photo ${index + 1}`}
                  className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                    <ImageIcon className="h-5 w-5 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto px-6 pb-16">
        <div className="bg-gradient-to-br from-[#12121a] to-[#1a1a24] border border-[#C6A14A]/20 rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-2xl font-light text-white mb-3">
            Like What You See?
          </h2>
          <p className="text-[#E8E4DE]/70 mb-6 max-w-lg mx-auto">
            Let us capture your property with the same professional quality.
          </p>
          <Link
            to="/book"
            className="inline-flex items-center gap-2 bg-[#C6A14A] text-[#0a0a0f] px-8 py-3 rounded-lg font-medium hover:bg-[#D4AF5A] transition-colors"
          >
            Book Your Shoot
          </Link>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && photos.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-3 text-white/60 hover:text-white z-10 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-4 text-white/60 text-sm bg-white/10 px-4 py-2 rounded-full">
            {lightboxIndex + 1} / {photos.length}
          </div>

          {/* Navigation */}
          <button
            onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white/60 hover:text-white z-10 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
          
          <button
            onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white/60 hover:text-white z-10 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <ChevronRight className="h-8 w-8" />
          </button>

          {/* Image */}
          <div
            className="max-w-[95vw] max-h-[90vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={photos[lightboxIndex]?.url}
              alt={`Photo ${lightboxIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
}
